import { HorizontalAlignment } from '@thg-commerce/enterprise-core'
import {
  css,
  keyframes,
  mq,
  spacing,
  styled,
  Text,
} from '@thg-commerce/enterprise-theme'
import { Grid, GridItem, Picture } from '@thg-commerce/gravity-system'

import { LogoImageCollectionThemeInterface } from '../theme'

const seamlessScrollAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-33.33%);
  }
`

export const Title = styled.h2`
  ${(props) =>
    Text(
      props.theme.widget.logoImageCollection.title.font.entry,
      'alternate',
      props.theme.widget.logoImageCollection.title.font.hasScaledText,
    )};
  margin-bottom: ${spacing(1)};
  text-align: inherit;
`

export const SubTitle = styled.p`
  ${Text('bodyText', 'default')};
  margin-bottom: ${spacing(3)};
  text-align: inherit;
`

export const Container = styled.div<{
  horizontalAlignment?: HorizontalAlignment
}>`
  text-align: ${(props) => props.horizontalAlignment || 'left'};
  width: 100%;
  max-width: ${(props) => props.theme.site.siteWidth};
  margin: 0 auto;

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    padding: ${spacing(1)} ${spacing(4)};
  }
`
export const LogoPictureContainer = styled.div<{
  textOnlyCarousel?: boolean
}>`
  padding: 0;
  width: 100%;
  max-width: ${(props) => props.theme.site.siteWidth};
  opacity: ${(props) => (props.textOnlyCarousel ? 1 : 0.8)};

  &:hover {
    opacity: 1;
  }
`

export const StyledGrid = styled(Grid)`
  margin: 0;
`

export const StyledGridItem = styled(GridItem)`
  margin: 0;
`

export const LogoWrapper = styled.ul<{
  horizontalAlignment?: HorizontalAlignment
  textOnlyCarousel?: boolean
  hasTitle?: boolean
}>`
  display: flex;
  flex-wrap: ${(props) => (props.textOnlyCarousel ? 'nowrap' : 'wrap')};
  justify-content: ${(props) => props.horizontalAlignment || 'left'};
  align-items: center;

  ${(props) =>
    props.textOnlyCarousel &&
    css`
      overflow: hidden;
      position: relative;
      width: 100%;
      padding: ${spacing(0.5)} 0;
      height: auto;
    `}

  ${(props) =>
    !props.textOnlyCarousel &&
    css`
      ${mq(props.theme.breakpointUtils.map, 'sm')} {
        margin-right: -${spacing(2)};
        margin-left: -${spacing(2)};
      }

      ${mq(props.theme.breakpointUtils.map, 'md')} {
        gap: ${spacing(1)};
      }
    `}
`

export const TextOnlyStaticWrapper = styled.ul<{
  horizontalAlignment?: HorizontalAlignment
}>`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${(props) => props.horizontalAlignment || 'center'};
  align-items: center;
  margin: 0;
  padding: ${spacing(0.5)} 0;
  list-style: none;

  /* Desktop: Single line layout */
  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    flex-wrap: nowrap;
    gap: ${spacing(2)};
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
    gap: ${spacing(3)};
  }

  li {
    flex-basis: calc(50% - ${spacing(1)});
    max-width: calc(50% - ${spacing(1)});
    margin-bottom: ${spacing(1)};
    text-align: center;
    padding: 0 ${spacing(0.5)};

    ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
      flex-basis: calc(50% - ${spacing(1)});
      max-width: calc(50% - ${spacing(1)});
    }

    ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
      flex-basis: auto;
      flex-grow: 0;
      flex-shrink: 1;
      width: auto;
      max-width: none;
      margin-bottom: 0;
      margin-right: 0;
      min-width: 0;
    }
  }
`

export const ScrollingLogoWrapper = styled.div`
  display: flex;
  white-space: nowrap;
  animation: ${seamlessScrollAnimation}
    ${(props) =>
      props.theme.widget?.logoImageCollection?.GlobalBrandLogos?.animation
        ?.desktop || '100s'}
    linear infinite;
  width: fit-content;

  &:hover {
    animation-play-state: paused;
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'xs')} {
    animation: ${seamlessScrollAnimation}
      ${(props) =>
        props.theme.widget?.logoImageCollection?.GlobalBrandLogos?.animation
          ?.mobile || '100s'}
      linear infinite;
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
    animation: ${seamlessScrollAnimation}
      ${(props) =>
        props.theme.widget?.logoImageCollection?.GlobalBrandLogos?.animation
          ?.desktop || '100s'}
      linear infinite;
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    animation: ${seamlessScrollAnimation}
      ${(props) =>
        props.theme.widget?.logoImageCollection?.GlobalBrandLogos?.animation
          ?.tablet || '100s'}
      linear infinite;
    margin-right: -${spacing(2)};
    margin-left: -${spacing(2)};
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    animation: ${seamlessScrollAnimation}
      ${(props) =>
        props.theme.widget?.logoImageCollection?.GlobalBrandLogos?.animation
          ?.desktop || '60s'}
      linear infinite;
    gap: ${spacing(1)};
  }
`

export const StyledPicture = styled(Picture)`
  width: 100%;
  max-width: 180px;
  aspect-ratio: auto 1 / 1;

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    max-width: 130px;
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
    max-width: 190px;
  }
`

export const LogoItem = styled.li<{ oddTotalItems: boolean }>`
  ${ScrollingLogoWrapper} & {
    display: inline-flex;
    flex: 0 0 auto;
    padding: 0 ${spacing(3)};
    min-width: 150px;
    width: auto;
    justify-content: center;
    align-items: center;
    white-space: nowrap;

    ${(props) => mq(props.theme.breakpointUtils.map, 'xs')} {
      padding: 0 ${spacing(1)};
      min-width: 120px;
    }

    ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
      padding: 0 ${spacing(1.5)};
      min-width: 140px;
    }

    ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
      padding: 0 ${spacing(2)};
      min-width: 160px;
    }
  }

  flex-basis: calc(50%);
  width: 100%;

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    // 3 items per row on tablet
    flex-basis: calc(33%);
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    // 6 items per row on laptop
    flex-basis: calc(16.6% - ${spacing(1)});
  }
`

export const StyledLink = styled.a`
  outline: none;
  text-decoration: none;

  display: block;
  border: 2px solid transparent;

  &:focus-visible {
    border: 2px solid ${(props) => props.theme.colors.palette.brand.base};
  }
`

export const LogoText = styled.p<{
  horizontalAlignment?: HorizontalAlignment
  theme: LogoImageCollectionThemeInterface
}>`
  ${Text('large1', 'alternate')};
  text-align: ${(props) => props.horizontalAlignment};

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    line-height: ${(props) =>
      props.theme.GlobalBrandLogos?.brandText?.sm?.lineHeight};
  }
`
